/**
 * Stacked-bar SVG chart for "runs per hour by status, last N hours".
 *
 * Pure SVG, no library. The data is small (≤168 buckets at 7d) and the
 * shapes are simple — adding recharts (~80KB gzipped) for this would
 * be ugly overkill.
 *
 * Layout:
 *   - Bars span the full width, evenly spaced.
 *   - Stacks order bottom→top: succeeded (success), failed (destructive),
 *     cancelled (muted), running (warning).
 *   - Y axis: 4 reference lines (max → 0), no decoration beyond a thin
 *     baseline.
 *   - Hover: shows a tooltip with the exact counts.
 *
 * Empty hours render as zero-height bars (just a baseline tick), so the
 * timeline stays unbroken even on quiet days.
 */
import * as React from "react";

import type { HourBucket } from "@/lib/api-dashboard";

interface RunsTimeseriesChartProps {
  buckets: HourBucket[];
  height?: number;
  /** Called with the clicked bucket so the parent can deep-link to /workflow-runs filtered by that hour. */
  onBucketClick?: (b: HourBucket) => void;
}

// Explicit palette — using CSS vars (with fallback) ended up rendering
// black because the tray-light/dark themes don't define --success etc.
// Hex constants keep the chart legible everywhere.
const COLORS = {
  succeeded: "#16a34a", // green-600
  failed: "#dc2626",    // red-600
  cancelled: "#6b7280", // gray-500
  running: "#d97706",   // amber-600
};

function fmtHour(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function fmtFullHour(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function RunsTimeseriesChart({
  buckets,
  height = 200,
  onBucketClick,
}: RunsTimeseriesChartProps) {
  const width = 800; // viewBox; scales fluidly via CSS
  const padding = { top: 12, right: 16, bottom: 22, left: 32 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const [hoverIdx, setHoverIdx] = React.useState<number | null>(null);

  const totals = buckets.map(
    (b) => b.succeeded + b.failed + b.running + b.cancelled,
  );
  const maxRaw = Math.max(...totals, 1);
  // Round up to a nice grid value (so the y-axis doesn't show 7, 14, 21).
  const niceMax = (() => {
    const exponent = Math.floor(Math.log10(maxRaw));
    const fraction = maxRaw / 10 ** exponent;
    const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
    return nice * 10 ** exponent;
  })();

  const barW = buckets.length > 0 ? innerW / buckets.length : 0;
  const barGap = Math.min(2, barW * 0.1);

  // Y axis ticks at 0, 1/4, 1/2, 3/4, max.
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((p) => Math.round(p * niceMax));

  function y(value: number): number {
    return padding.top + innerH - (value / niceMax) * innerH;
  }

  return (
    <div className="relative w-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="block w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label="Runs per hour by status"
      >
        {/* Y-axis gridlines */}
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={y(t)}
              y2={y(t)}
              stroke="currentColor"
              strokeOpacity={0.1}
            />
            <text
              x={padding.left - 4}
              y={y(t) + 3}
              textAnchor="end"
              fontSize={9}
              fill="currentColor"
              opacity={0.5}
            >
              {t}
            </text>
          </g>
        ))}

        {/* Bars */}
        {buckets.map((b, i) => {
          const xCenter = padding.left + i * barW + barW / 2;
          const xLeft = xCenter - (barW - barGap) / 2;
          const bw = barW - barGap;

          // Stack: succeeded, cancelled, failed, running (bottom→top)
          let cursor = padding.top + innerH;
          const segments = [
            { key: "succeeded", n: b.succeeded, color: COLORS.succeeded },
            { key: "cancelled", n: b.cancelled, color: COLORS.cancelled },
            { key: "failed", n: b.failed, color: COLORS.failed },
            { key: "running", n: b.running, color: COLORS.running },
          ];

          return (
            <g
              key={b.hour}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              onClick={() => onBucketClick?.(b)}
              style={{ cursor: onBucketClick ? "pointer" : "default" }}
            >
              {/* invisible hover hitbox: full column */}
              <rect
                x={padding.left + i * barW}
                y={padding.top}
                width={barW}
                height={innerH}
                fill={hoverIdx === i ? "currentColor" : "transparent"}
                fillOpacity={hoverIdx === i ? 0.04 : 0}
              />
              {segments.map((s) => {
                if (s.n === 0) return null;
                const h = (s.n / niceMax) * innerH;
                cursor -= h;
                return (
                  <rect
                    key={s.key}
                    x={xLeft}
                    y={cursor}
                    width={Math.max(bw, 1)}
                    height={h}
                    fill={s.color}
                  />
                );
              })}
              {/* zero-height tick so quiet hours stay visible */}
              {totals[i] === 0 && (
                <rect
                  x={xLeft}
                  y={padding.top + innerH - 1}
                  width={Math.max(bw, 1)}
                  height={1}
                  fill="currentColor"
                  opacity={0.2}
                />
              )}
            </g>
          );
        })}

        {/* X-axis labels (every 4 hours so it stays readable) */}
        {buckets.map((b, i) => {
          if (i % Math.ceil(buckets.length / 8) !== 0) return null;
          const xCenter = padding.left + i * barW + barW / 2;
          return (
            <text
              key={b.hour}
              x={xCenter}
              y={height - 6}
              textAnchor="middle"
              fontSize={9}
              fill="currentColor"
              opacity={0.5}
            >
              {fmtHour(b.hour)}
            </text>
          );
        })}

        {/* Baseline */}
        <line
          x1={padding.left}
          x2={width - padding.right}
          y1={y(0)}
          y2={y(0)}
          stroke="currentColor"
          strokeOpacity={0.4}
        />
      </svg>

      {/* Tooltip */}
      {hoverIdx !== null && buckets[hoverIdx] && (
        <div
          className="pointer-events-none absolute top-2 right-2 rounded-md border border-border/60 bg-elevated shadow-md px-2 py-1.5 text-xs font-mono"
          style={{ minWidth: 160 }}
        >
          <div className="mb-1 font-medium text-foreground">
            {fmtFullHour(buckets[hoverIdx].hour)}
          </div>
          <div className="flex items-center justify-between gap-3">
            <span style={{ color: COLORS.succeeded }}>succeeded</span>
            <span>{buckets[hoverIdx].succeeded}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span style={{ color: COLORS.failed }}>failed</span>
            <span>{buckets[hoverIdx].failed}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span style={{ color: COLORS.running }}>running</span>
            <span>{buckets[hoverIdx].running}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span style={{ color: COLORS.cancelled }}>cancelled</span>
            <span>{buckets[hoverIdx].cancelled}</span>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {Object.entries(COLORS).map(([k, c]) => (
          <span key={k} className="inline-flex items-center gap-1">
            <span
              className="inline-block h-2 w-2"
              style={{ backgroundColor: c }}
            />
            {k}
          </span>
        ))}
      </div>
    </div>
  );
}
